import React, { forwardRef, useMemo } from 'react';
import type {
    GroupBase,
    MenuPlacement,
    MenuPosition,
    SelectComponentsConfig,
    StylesConfig,
} from 'react-select';
import Select from 'react-select';
import SelectBase from 'react-select/base';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

// style
import defaultStyleConfig from '../shared/AampeCreateSelectDropdown/select-style';
// components
import InputError from './inputError';
import AampeInputLabel from './inputLabel';
import SelectInfo from './selectInfo';
import SingleValue from './selectSingleValue';
import SingleValueCompact from './selectSingleValueCompact';

export interface SearchableSelectType {
    value: string | number;
    label: string;
    info?: React.ReactNode;
    disabled?: boolean;
    trailingIcon?: React.ReactNode;
    isHighlighted?: boolean;
}

export interface SelectOptionExtended extends SearchableSelectType {
    componentTypeId?: string;
}

export interface GroupedOption {
    label: string;
    options: SearchableSelectType[];
}

export interface SearchableSelectBaseProps {
    label?: string;
    labelStyle?: React.CSSProperties;
    name: string;
    options: SearchableSelectType[] | GroupedOption[];
    defaultValue?: SearchableSelectType[] | SearchableSelectType;
    placeholder?: string;
    value?: SearchableSelectType[] | SearchableSelectType;
    onChange: (
        field: string,
        value: SearchableSelectType[] | SearchableSelectType,
    ) => void;
    onBlur?: (field: string, isTouched?: boolean) => void;
    onInputChange?: (newValue: string, actionMeta: any) => void;
    error?: string;
    touched?: boolean;
    layout?: 'horizontal' | 'vertical';
    isCompact?: boolean;
    isDisabled?: boolean;
    customStyles?: StylesConfig<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    >;
    menuIsOpen?: boolean;
    menuShouldBlockScroll?: boolean;
    ref?: any;
    menuPortalTarget?: HTMLElement | null;
    components?: SelectComponentsConfig<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    >;
    isSearchable?: boolean;
    className?: string;
    footer?: React.ReactNode;
    menuPlacement?: MenuPlacement;
    menuPosition?: MenuPosition;
    onMenuScrollToBottom?: () => void;
    addRightBorder?: boolean;
    containerStyles?: React.CSSProperties;
    isOptionDisabled?: (option: SearchableSelectType) => boolean;
    noOptionsMessage?: () => React.ReactNode;
}

interface SearchableSelectProps extends SearchableSelectBaseProps {
    isMultipleValue?: boolean;
    isLoading?: boolean;
}

const SearchableSelect: React.FC<
    React.PropsWithChildren<SearchableSelectProps>
> = forwardRef<
    SelectBase<SearchableSelectType, boolean, GroupBase<SearchableSelectType>>,
    SearchableSelectProps
>(
    (
        {
            placeholder,
            name,
            label,
            labelStyle,
            value,
            isMultipleValue,
            isLoading,
            onChange,
            onInputChange,
            onBlur,
            error,
            touched,
            options,
            defaultValue,
            layout = 'vertical',
            isCompact = false,
            isDisabled = false,
            customStyles = {},
            menuIsOpen,
            menuShouldBlockScroll = false,
            menuPortalTarget,
            components,
            isSearchable = true,
            className,
            footer,
            menuPlacement,
            menuPosition,
            onMenuScrollToBottom,
            addRightBorder,
            containerStyles,
            isOptionDisabled,
            noOptionsMessage,
        },
        ref,
    ) => {
        const handleChange = (currentValue: any) => {
            if (isMultipleValue) {
                const previousSelected = value;
                if (
                    previousSelected &&
                    Array.isArray(previousSelected) &&
                    !previousSelected.some(
                        (option) => option.value === currentValue.value,
                    )
                ) {
                    onChange(name, [...previousSelected, currentValue]);
                }
            } else {
                onChange(name, currentValue);
            }
        };

        const handleBlur = () => {
            if (onBlur) {
                onBlur(name, true);
            }
        };

        const customComponent = useMemo<
            SelectComponentsConfig<
                SearchableSelectType,
                boolean,
                GroupBase<SearchableSelectType>
            >
        >(() => {
            let customComponents = {
                IndicatorSeparator: () => null,
                Option: SelectInfo,
                SingleValue: isCompact ? SingleValueCompact : SingleValue,
                ...(isDisabled && { DropdownIndicator: undefined }),
                ...components,
            };
            if (footer) {
                customComponents = {
                    ...customComponents,
                    MenuList: (props: any) => (
                        <>
                            <div>{props.children}</div>
                            {footer}
                        </>
                    ),
                };
            }
            return customComponents;
        }, [footer, components, isCompact]);

        return (
            <SelectContainer
                isRightBorder={addRightBorder}
                layout={layout}
                style={containerStyles}
            >
                {label &&
                    (layout == 'vertical' ? (
                        <AampeInputLabel label={label} style={labelStyle} />
                    ) : (
                        <HorizontalLabelContainer>
                            <AampeInputLabel label={label} style={labelStyle} />
                        </HorizontalLabelContainer>
                    ))}
                <Select
                    value={value}
                    ref={ref}
                    name={name}
                    isMulti={isMultipleValue}
                    controlShouldRenderValue={!isMultipleValue && !menuIsOpen}
                    menuIsOpen={menuIsOpen}
                    onChange={handleChange}
                    onInputChange={onInputChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    options={options}
                    defaultValue={defaultValue}
                    styles={{ ...defaultStyleConfig, ...customStyles }}
                    isDisabled={isDisabled}
                    isOptionDisabled={
                        isOptionDisabled ?? ((option) => !!option.disabled)
                    }
                    menuShouldBlockScroll={menuShouldBlockScroll}
                    openMenuOnFocus={!!ref}
                    components={customComponent}
                    menuPortalTarget={menuPortalTarget}
                    isSearchable={isSearchable}
                    className={className}
                    isLoading={isLoading}
                    menuPlacement={menuPlacement}
                    menuPosition={menuPosition}
                    onMenuScrollToBottom={onMenuScrollToBottom}
                    noOptionsMessage={noOptionsMessage}
                />
                {error && touched && <InputError error={error} />}
            </SelectContainer>
        );
    },
);

export default SearchableSelect;
const SelectContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => !['layout', 'isRightBorder'].includes(prop),
})<{
    layout: 'horizontal' | 'vertical';
    isRightBorder?: boolean;
}>(({ layout, isRightBorder }) => ({
    display: layout === 'horizontal' ? 'flex' : 'block',
    alignItems: layout === 'horizontal' ? 'center' : undefined,
    paddingLeft: isRightBorder ? aampeTheme.spacing(1) : '0px',
    borderRight: isRightBorder
        ? `1px solid ${aampeTheme.colors.interactiveWG01}`
        : '0px',
    width: '100%',
}));

const HorizontalLabelContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    padding: aampeTheme.spacing(1, 1, 0, 0),
    height: aampeTheme.size.filterHeight.default,
});
